import React, { useState } from 'react';
import { Undo2, Redo2, Grid3X3, Magnet, Download, MoreHorizontal, ChevronLeft } from 'lucide-react';
import { useCanvasStore } from '../../store/canvasStore';
import { useEditorStore } from '../../store/editorStore';
import { redoEditor, undoEditor } from '../../store/historyActions';
import type { FloorPlan } from '../../types';

interface TopBarProps {
  floorPlan: FloorPlan;
  onUpdate: (fp: FloorPlan) => void;
  onBackToProjects: () => void;
  onExport: () => void;
  onHistoryChange?: () => void;
}

const TopBar: React.FC<TopBarProps> = ({ floorPlan, onUpdate, onBackToProjects, onExport, onHistoryChange }) => {
  const [editingName, setEditingName] = useState(false);
  const [nameInput, setNameInput] = useState(floorPlan.name);
  const { history, historyIndex } = useCanvasStore();
  const { gridVisible, setGridVisible, snapEnabled, setSnapEnabled, draftWallPoints } = useEditorStore();
  const tracingWall = (draftWallPoints?.length ?? 0) > 0;
  const canUndo = tracingWall || historyIndex > 0;
  const canRedo = !tracingWall && historyIndex < history.length - 1;

  const handleUndo = () => {
    const kind = undoEditor();
    if (kind === 'canvas') onHistoryChange?.();
  };
  const handleRedo = () => {
    if (redoEditor()) onHistoryChange?.();
  };

  const handleNameBlur = () => {
    setEditingName(false);
    const trimmed = nameInput.trim() || 'Untitled Floor Plan';
    if (trimmed !== floorPlan.name) {
      onUpdate({ ...floorPlan, name: trimmed, updated_at: new Date().toISOString() });
    }
  };

  return (
    <div
      className="flex items-center justify-between h-14 px-4 shrink-0 border-b"
      style={{ backgroundColor: '#1c1c28', borderColor: '#2e2e40' }}
    >
      <div className="flex items-center gap-3">
        <button
          onClick={onBackToProjects}
          className="p-2 rounded-lg hover:bg-white/5 transition-colors"
          style={{ color: '#8888aa' }}
          aria-label="Back to projects"
        >
          <ChevronLeft size={20} />
        </button>
        {editingName ? (
          <input
            value={nameInput}
            onChange={(e) => setNameInput(e.target.value)}
            onBlur={handleNameBlur}
            onKeyDown={(e) => e.key === 'Enter' && handleNameBlur()}
            className="px-2 py-1 rounded text-base font-medium focus:outline-none focus:ring-1"
            style={{ backgroundColor: '#111118', color: '#f0f0f5', border: '1px solid #2e2e40' }}
            autoFocus
          />
        ) : (
          <button
            onClick={() => setEditingName(true)}
            className="text-base font-medium hover:bg-white/5 px-2 py-1 rounded transition-colors"
            style={{ color: '#f0f0f5' }}
          >
            {floorPlan.name}
          </button>
        )}
      </div>

      <div className="flex items-center gap-2">
        <button
          onClick={handleUndo}
          disabled={!canUndo}
          className="p-2 rounded-lg transition-colors disabled:opacity-40"
          style={{ color: canUndo ? '#f0f0f5' : '#555' }}
          title="Undo (Ctrl+Z)"
        >
          <Undo2 size={18} />
        </button>
        <button
          onClick={handleRedo}
          disabled={!canRedo}
          className="p-2 rounded-lg transition-colors disabled:opacity-40"
          style={{ color: canRedo ? '#f0f0f5' : '#555' }}
          title="Redo (Ctrl+Shift+Z)"
        >
          <Redo2 size={18} />
        </button>
        <div className="w-px h-6" style={{ backgroundColor: '#2e2e40' }} />
        <button
          onClick={() => setGridVisible(!gridVisible)}
          className={`p-2 rounded-lg transition-colors ${gridVisible ? 'bg-[#FF4C4C]/20' : ''}`}
          style={{ color: gridVisible ? '#FF4C4C' : '#8888aa' }}
          title="Toggle grid"
        >
          <Grid3X3 size={18} />
        </button>
        <button
          onClick={() => setSnapEnabled(!snapEnabled)}
          className={`p-2 rounded-lg transition-colors ${snapEnabled ? 'bg-[#FF4C4C]/20' : ''}`}
          style={{ color: snapEnabled ? '#FF4C4C' : '#8888aa' }}
          title="Toggle snap"
        >
          <Magnet size={18} />
        </button>
        <div className="w-px h-6" style={{ backgroundColor: '#2e2e40' }} />
        <button
          onClick={onExport}
          className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors"
          style={{ backgroundColor: '#FF4C4C', color: '#fff' }}
        >
          <Download size={16} />
          Export
        </button>
      </div>
    </div>
  );
};

export default TopBar;
